import React, { useState, useEffect } from 'react';
import DatabaseService from '../services/DatabaseService';

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('QuotationDetailsModal Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">เกิดข้อผิดพลาด</h5>
                <button type="button" className="btn-close" onClick={this.props.onClose}></button>
              </div>
              <div className="modal-body text-center">
                <div className="alert alert-danger">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  เกิดข้อผิดพลาดในการแสดงรายละเอียด กรุณาลองใหม่อีกครั้ง
                </div>
                <button className="btn btn-primary" onClick={() => window.location.reload()}>
                  รีเฟรชหน้า
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const QuotationDetailsModal = ({ quotation, isOpen, onClose, onUpdate }) => {
  const [saleData, setSaleData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pmStats, setPmStats] = useState({});

  // ดึงข้อมูลการขายที่เกี่ยวข้องกับใบเสนอราคา
  useEffect(() => {
    if (quotation && isOpen) {
      fetchSaleData();
    }
  }, [quotation, isOpen]);

  const fetchSaleData = async () => {
    setLoading(true);
    try {
      // ค้นหาข้อมูลการขายที่เกี่ยวข้องกับใบเสนอราคานี้
      const response = await fetch(`/api/sales?quotationId=${quotation.id}`);
      if (response.ok) {
        const sales = await response.json();
        if (sales.length > 0) {
          // ดึงรายละเอียดของการขายแรกที่พบ
          const saleResponse = await fetch(`/api/sales/${sales[0].id}`);
          if (saleResponse.ok) {
            const saleDetail = await saleResponse.json();
            setSaleData(saleDetail);
            
            // Fetch PM stats for each serial number
            if (saleDetail.items && Array.isArray(saleDetail.items)) {
              const pmStatsData = {};
              for (const item of saleDetail.items) {
                if (item.serial_number) {
                  try {
                    const pmResponse = await fetch(`/api/pm-services/stats/serial/${item.serial_number}`);
                    if (pmResponse.ok) {
                      const pmData = await pmResponse.json();
                      pmStatsData[item.serial_number] = pmData;
                    }
                  } catch (error) {
                    console.error(`Error fetching PM stats for ${item.serial_number}:`, error);
                  }
                }
              }
              setPmStats(pmStatsData);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching sale data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // แสดง loading หรือ error state
  if (!quotation) {
    return (
      <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">รายละเอียดใบเสนอราคา</h5>
              <button type="button" className="btn-close" onClick={onClose}></button>
            </div>
            <div className="modal-body text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">กำลังโหลด...</span>
              </div>
              <p className="mt-2">กำลังโหลดข้อมูล...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const getProductTypeLabel = (type) => {
    switch (type) {
      case 'battery': return 'แบตเตอรี่';
      case 'charger': return 'แบตเตอรี่';
      case 'mixed': return 'ผสม';
      default: return type || 'ไม่ระบุ';
    }
  };

  return (
    <ErrorBoundary onClose={onClose}>
      <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            {/* Header */}
            <div className="modal-header" style={{ backgroundColor: '#4285f4', color: 'white' }}>
              <div className="d-flex align-items-center">
                <i className="fas fa-user me-2" style={{ fontSize: '1.2rem' }}></i>
                <h5 className="modal-title mb-0">ข้อมูลลูกค้า</h5>
              </div>
              <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
            </div>

            <div className="modal-body p-0">
              {/* ข้อมูลลูกค้าและใบเสนอราคา */}
              <div className="row g-0">
                {/* ข้อมูลลูกค้า */}
                <div className="col-md-6 p-4" style={{ backgroundColor: '#f8f9fa' }}>
                  <div className="mb-3">
                    <div className="fw-bold mb-2">ชื่อ: {quotation.customer_name || 'ไม่ระบุ'}</div>
                    <div className="mb-2">เบอร์โทร: {quotation.customer_phone || 'ไม่ระบุ'}</div>
                    <div className="mb-2">ที่อยู่: {quotation.customer_address || 'ไม่ระบุ'}</div>
                  </div>
                </div>

                {/* ข้อมูลใบเสนอราคา */}
                <div className="col-md-6 p-4">
                  <div className="mb-3">
                    <div className="fw-bold mb-2">เลขที่ใบเสนอราคา: {quotation.quotationNumber || `QT-${String(quotation.id).padStart(8, '0')}`}</div>
                    <div className="mb-2">ยอดรวม: ฿{quotation.total_amount ? quotation.total_amount.toLocaleString('th-TH', {minimumFractionDigits: 2}) : '0.00'}</div>
                    <div className="mb-2">วันที่ปิดการขาย: {quotation.created_at ? DatabaseService.formatDate(quotation.created_at) : 'ไม่ระบุ'}</div>
                  </div>
                </div>
              </div>

              {/* ซีเรียลนัมเบอร์ที่เลือก */}
              <div className="px-4 py-3" style={{ backgroundColor: '#e3f2fd' }}>
                <div className="d-flex align-items-center mb-3">
                  <div className="bg-primary text-white px-2 py-1 rounded me-2" style={{ fontSize: '0.8rem' }}>
                    <i className="fas fa-barcode me-1"></i>
                  </div>
                  <h6 className="mb-0 fw-bold">ซีเรียลนัมเบอร์ที่เลือก</h6>
                </div>

                {/* แสดงซีเรียลนัมเบอร์ */}
                <div className="row g-2">
                  <div className="col-12">
                    {loading ? (
                      <div className="text-muted">กำลังโหลดข้อมูล...</div>
                    ) : saleData && saleData.items && Array.isArray(saleData.items) && saleData.items.length > 0 ? (
                      saleData.items.filter(item => item.serial_number).map((item, itemIndex) => (
                        <div key={`serial-${itemIndex}`} className="mb-2">
                          <div className="d-flex align-items-center">
                            <span className="me-2">{getProductTypeLabel(item.product_type)} - {item.product_name || item.product_type}</span>
                            <span className="badge bg-primary me-1">
                              {item.serial_number}
                            </span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-muted">ไม่มีข้อมูลซีเรียลนัมเบอร์</div>
                    )}
                  </div>
                </div>
              </div>

              {/* PM Service */}
              <div className="px-4 py-3" style={{ backgroundColor: '#f8f9fa' }}>
                <div className="d-flex align-items-center mb-3">
                  <div className="bg-primary text-white px-2 py-1 rounded me-2" style={{ fontSize: '0.8rem' }}>
                    <i className="fas fa-tools me-1"></i>
                  </div>
                  <h6 className="mb-0 fw-bold">PM Service</h6>
                </div>

                {/* แสดงข้อมูล PM Service */}
                <div className="row g-2">
                  {loading ? (
                    <div className="text-muted">กำลังโหลดข้อมูล...</div>
                  ) : saleData && saleData.items && Array.isArray(saleData.items) && saleData.items.length > 0 ? (
                    saleData.items.filter(item => item.serial_number && item.pm_service_count > 0).map((item, itemIndex) => {
                      const pmStat = pmStats[item.serial_number];
                      const completed = pmStat ? pmStat.completed_services : 0;
                      const total = item.pm_service_count;
                      const isComplete = completed >= total;
                      
                      return (
                        <div key={`pm-${itemIndex}`} className="col-md-6">
                          <div className="d-flex align-items-center justify-content-between">
                            <span className="me-2">{getProductTypeLabel(item.product_type)} - {item.serial_number}</span>
                            <div>
                              <span className={`badge ${isComplete ? 'bg-success' : 'bg-warning text-dark'}`}>
                                {completed}/{total}
                              </span>
                              {isComplete && (
                                <small className="text-success fw-bold ms-1">ครบ</small>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-muted">ไม่มีข้อมูล PM Service</div>
                  )}
                </div>
              </div>

              {/* ระยะเวลารับประกัน */}
              <div className="px-4 py-3">
                <div className="d-flex align-items-center mb-3">
                  <div className="bg-primary text-white px-2 py-1 rounded me-2" style={{ fontSize: '0.8rem' }}>
                    <i className="fas fa-shield-alt me-1"></i>
                  </div>
                  <h6 className="mb-0 fw-bold">ระยะเวลารับประกัน</h6>
                </div>

                {/* แสดงข้อมูลการรับประกัน */}
                <div className="row g-2">
                  {loading ? (
                    <div className="text-muted">กำลังโหลดข้อมูล...</div>
                  ) : saleData && saleData.items && Array.isArray(saleData.items) && saleData.items.length > 0 ? (
                    saleData.items.map((item, itemIndex) => {
                      const warrantyMonths = item.warranty_period || (item.product_type === 'battery' ? 60 : 10);
                      return (
                        <div key={`warranty-${itemIndex}`} className="col-md-6">
                          <div className="d-flex align-items-center">
                            <span className="me-2">{getProductTypeLabel(item.product_type)} - {item.product_name || item.product_type}</span>
                            <span className="badge bg-success">
                              <i className="fas fa-calendar-alt me-1"></i>
                              {warrantyMonths} เดือน
                            </span>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-muted">ไม่มีข้อมูลการรับประกัน</div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                <i className="fas fa-times me-1"></i>
                ปิด
              </button>
              <button type="button" className="btn btn-warning">
                <i className="fas fa-edit me-1"></i>
                แก้ไข
              </button>
            </div>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default QuotationDetailsModal;